import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";

import { makeStyles, Tab, Tabs } from "@material-ui/core";
import ExpandMoreOutlinedIcon from "@material-ui/icons/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@material-ui/icons/ExpandLessOutlined";

import { BetaBadge, NewBadge } from "../../../components/common/FeatureBadges";

import { getEnv, isAuthorized } from "../../../utils/appUtils";
import { AclType } from "../../../utils/constants";
import { getInitialTab, getLastValueFromUrl } from "../../../utils/utils";

import AssetTypePanel from "./scheduling/AssetTypePanel";
import BrandingPanel from "./BrandingPanel";
import ChecklistPanel from "./ChecklistPanel";
import DocumentsPanel from "./DocumentsPanel";
import EventsTypePanel from "./scheduling/EventsTypePanel";
import GeoFencePanel from "./GeoFencePanel";
import DriverCheckInPanel from "./DriverCheckInPanel";
import InvoicingPanel from "./InvoicingPanel";
import { SchedulePanel } from "./scheduling/SchedulePanel";
import ZapierPanel from "./ZapierPanel";

const useStyles = makeStyles((theme) => ({
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: "16em",
    "& span:nth-child(1)": {
      flexDirection: "row",
      justifyContent: "flex-end",
      marginRight: theme.spacing(2),
    },
  },
  tabsContainer: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    width: "100%",
    marginTop: theme.spacing(3),
  },
  hiddenTab: {
    display: "none",
    width: "0%",
  },
  visibleTab: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginLeft: theme.spacing(3),
  },
}));

const YardSettingsTab = ({ disabled, ...props }) => {
  const myPath = "settings";
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(getInitialTab(myPath));
  const [expandedSchedule, setExpandedSchedule] = useState(false);
  let tabIndex = 0;

  useEffect(() => {
    const currentPath = getLastValueFromUrl(location.pathname);
    const isTabActive = currentPath === myPath;

    if (isTabActive) {
      history.replace({
        pathname: location.pathname,
        search: `?t=${tabValue}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-expand Schedule group if navigated directly to its submenu
  useEffect(() => {
    const ids = [];
    if (isAuthorized(AclType.INVOICES)) ids.push("invoicing");
    ids.push(
      "branding",
      "documents",
      "schedule",
      "assets",
      "events",
      "checklist",
      "zapier",
    );
    const scheduleIndex = ids.indexOf("schedule");
    const assetsIndex = ids.indexOf("assets");
    const eventsIndex = ids.indexOf("events");
    const inScheduleGroup =
      tabValue === scheduleIndex ||
      tabValue === assetsIndex ||
      tabValue === eventsIndex;
    setExpandedSchedule((prev) => {
      if (inScheduleGroup && !prev) return true;
      if (!inScheduleGroup && prev) return false;
      return prev;
    });
  }, [tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    history.replace(`${location.pathname}?t=${newValue}`);
  };

  return (
    <div className={classes.tabsContainer}>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Settings Tabs"
        className={classes.tabs}
      >
        {isAuthorized(AclType.INVOICES) && (
          <Tab id="invoicing" label="Invoicing" />
        )}
        <Tab id="branding" label="Branding" />
        <Tab id="documents" label="Documents" />
        <Tab
          id="schedule"
          label={
            <span
              style={{ display: "flex", alignItems: "center", marginRight: 0 }}
            >
              <span>Schedule</span>
              {expandedSchedule ? (
                <ExpandLessOutlinedIcon fontSize="small" />
              ) : (
                <ExpandMoreOutlinedIcon fontSize="small" />
              )}
            </span>
          }
        />
        <Tab
          id="assets"
          label={<span>Assets</span>}
          style={{ display: expandedSchedule ? undefined : "none" }}
        />
        <Tab
          id="events"
          label={<span>Events</span>}
          style={{ display: expandedSchedule ? undefined : "none" }}
        />
        <Tab
          id="checklist"
          label={
            <>
              <BetaBadge /> Checklist
            </>
          }
        />
        <Tab
          id="zapier"
          label={
            <>
              <NewBadge /> Zapier
            </>
          }
        />
        <Tab
          id="geofence"
          label={
            <>
              <BetaBadge /> Geo-fence
            </>
          }
        />
        <Tab
          id="driver-checkin"
          label={
            <>
              <BetaBadge /> Driver check-in
            </>
          }
        />
      </Tabs>
      {isAuthorized(AclType.INVOICES) && (
        <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
          <InvoicingPanel {...props} disabled={disabled} />
        </TabPanel>
      )}
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <BrandingPanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <DocumentsPanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <SchedulePanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <AssetTypePanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <EventsTypePanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <ChecklistPanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <ZapierPanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <GeoFencePanel {...props} disabled={disabled} />
      </TabPanel>
      <TabPanel value={tabValue} index={tabIndex++} classes={classes}>
        <DriverCheckInPanel {...props} disabled={disabled} />
      </TabPanel>
    </div>
  );
};

const TabPanel = ({ classes, children, value, index, ...other }) => {
  return (
    <div
      // Hide, do not unmount, inactive tab content - linked to form.pristine
      className={value === index ? classes.visibleTab : classes.hiddenTab}
      role="tabpanel"
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      // hidden={value !== index}
      {...other}
    >
      {children}
    </div>
  );
};

export default YardSettingsTab;
